import React, { useEffect, useState } from 'react';
import { personalInfo, skills, education } from '../data/mockData';
import { MapPin, Mail, ArrowDown, ExternalLink, GraduationCap, Award } from 'lucide-react';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="overview" className="min-h-screen flex items-center pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6 w-full">
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Greeting */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-emerald-500"></div>
            <span className="font-mono text-sm text-emerald-600 dark:text-emerald-400 tracking-wide">
              Hello, I'm
            </span>
          </div>

          {/* Name */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white leading-tight mb-4">
            {personalInfo.name}
            <span className="text-emerald-500">.</span>
          </h1>

          {/* Title */}
          <h2 className="text-2xl sm:text-3xl text-gray-500 dark:text-gray-400 font-light mb-6">
            {personalInfo.subtitle}
          </h2>

          {/* Bio */}
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed mb-8">
            {personalInfo.bio}
          </p>

          {/* Quick Info */}
          <div className="flex flex-wrap items-center gap-4 mb-10">
            <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <MapPin className="h-4 w-4 text-emerald-500" />
              {personalInfo.location}
            </span>
            <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Mail className="h-4 w-4 text-emerald-500" />
              {personalInfo.email}
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-16">
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
            >
              Connect on LinkedIn
              <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 text-sm font-medium rounded-lg transition-colors duration-200"
            >
              Get in Touch
            </a>
          </div>

          <Separator className="mb-12 bg-gray-200 dark:bg-white/10" />

          {/* Skills Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-4">
                Languages
              </h3>
              <div className="flex flex-wrap gap-2">
                {skills.languages.map((skill) => (
                  <Badge
                    key={skill}
                    variant="secondary"
                    className="bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 border-0 font-normal hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-4">
                Frameworks
              </h3>
              <div className="flex flex-wrap gap-2">
                {skills.frameworks.map((skill) => (
                  <Badge
                    key={skill}
                    variant="secondary"
                    className="bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 border-0 font-normal hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-4">
                Tools & Platforms
              </h3>
              <div className="flex flex-wrap gap-2">
                {skills.tools.map((skill) => (
                  <Badge
                    key={skill}
                    variant="secondary"
                    className="bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 border-0 font-normal hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Education */}
          <div className="p-6 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/10">
                <GraduationCap className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{education.degree}</h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">{education.duration}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {education.institution} · CGPA: {education.cgpa}
                </p>
                <div className="flex flex-wrap gap-4">
                  {education.achievements.map((a, i) => (
                    <span key={i} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Award className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-16">
            <a
              href="#experience"
              className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors animate-bounce"
            >
              <span className="text-xs font-mono">scroll</span>
              <ArrowDown className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;